import { useEffect } from "react";

import type { MeetingModelJson } from "@performyard/service-contracts";
import type { GetUserCalendarIntegrationStatusResponse } from "@performyard/service-contracts";

import {
	useCompanyCalendarIntegrationSettings,
	useUserCalendarIntegrationStatus,
} from "../../../api/hooks/defs/calendar-integration";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

/**
 * Custom hook to manage main navigation active state for meeting workspace details table view.
 * Dynamically sets the correct navigation item based on admin status and participation.
 */
export const useWorkspaceNavigationState = (params: { isAdmin: boolean; isParticipant: boolean }): void => {
	const { isAdmin, isParticipant } = params;

	useEffect(() => {
		const updateMainNavigation = (): void => {
			const allNavItems = document.querySelectorAll("#header .links .top-item");
			allNavItems.forEach((item) => item.classList.remove("active"));

			if (isAdmin && !isParticipant) {
				const companyNavItem = document.querySelector('#header .links a[title="Company Info"]');
				companyNavItem?.classList.add("active");
			} else {
				const meetingWorkspacesNavItem = document.querySelector(
					'#header .links a[href="/meeting-workspaces/"]'
				);
				meetingWorkspacesNavItem?.classList.add("active");
			}
		};

		updateMainNavigation();
	}, [isAdmin, isParticipant]);
};

/**
 * Transform workspace participants for AvatarGroup component.
 */
export const transformWorkspaceParticipants = (
	participants: Array<{ userId: string; name?: string }> = []
): Array<{ _id: string; name: string }> =>
	participants.map((participant) => ({
		_id: participant.userId,
		name: participant.name || "",
	}));

const SUPPORTED_CALENDAR_PROVIDERS = ["google", "outlook"] as const;

type CalendarIntegrationStatus = {
	isCalendarIntegrationEnabled: boolean;
	userCalendarStatus: GetUserCalendarIntegrationStatusResponse | undefined;
};

/**
 * Custom hook to check if calendar integration is enabled for the current user.
 * Consolidates the calendar integration check logic for reuse across components.
 */
export const useCalendarIntegrationStatus = (): CalendarIntegrationStatus => {
	const { value: ffIsCalendarIntegrationEnabled } = useFeatureFlagQuery("ff-is-calendar-integration-enabled", false);
	const { data: companyCalendarIntegrationSettings } = useCompanyCalendarIntegrationSettings({
		enabled: ffIsCalendarIntegrationEnabled,
	});
	const { data: userCalendarStatus } = useUserCalendarIntegrationStatus({
		enabled: ffIsCalendarIntegrationEnabled && companyCalendarIntegrationSettings?.enabled === true,
	});

	const isCalendarIntegrationEnabled = Boolean(
		ffIsCalendarIntegrationEnabled &&
		companyCalendarIntegrationSettings?.enabled &&
		userCalendarStatus?.isConnected &&
		SUPPORTED_CALENDAR_PROVIDERS.includes(
			userCalendarStatus?.provider as (typeof SUPPORTED_CALENDAR_PROVIDERS)[number]
		) &&
		userCalendarStatus?.selectedCalendarId
	);

	return { isCalendarIntegrationEnabled, userCalendarStatus };
};

type MeetingCalendarIntegrationState = {
	isCalendarIntegrationEnabled: boolean;
	hasCalendarEvent: boolean;
	isCalendarMismatch: boolean;
};

/**
 * Custom hook to determine calendar integration state for a meeting.
 * Extracts complex calendar logic from components for better testability and reuse.
 */
export const useMeetingCalendarIntegration = (meeting: MeetingModelJson): MeetingCalendarIntegrationState => {
	const { isCalendarIntegrationEnabled, userCalendarStatus } = useCalendarIntegrationStatus();

	const hasCalendarEvent = Boolean(meeting.calendarEvent?.eventId);

	const isCalendarMismatch = Boolean(
		meeting.calendarEvent?.eventId &&
		meeting.calendarEvent?.provider &&
		userCalendarStatus &&
		!(
			userCalendarStatus.provider === meeting.calendarEvent.provider &&
			userCalendarStatus.selectedCalendarId === meeting.calendarEvent.calendarId
		)
	);

	return { isCalendarIntegrationEnabled, hasCalendarEvent, isCalendarMismatch };
};
